//orders / editOrders.js
import React, { useState, useEffect } from "react";
import { CircularProgress } from "@mui/material";

import axios from "../../services/http";

export default function EditOrders({ order, onClose }) {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (order) {
      // Process items first to ensure correct amount calculations
      const processedItems = (order.items || []).map((item) => ({
        ...item,
        qty: Number(item.qty) || 0,
        price: Number(item.price) || 0,
        amount: (Number(item.qty) || 0) * (Number(item.price) || 0),
      }));

      // Calculate initial total from processed items
      const initialTotal = processedItems.reduce(
        (sum, item) => sum + item.qty * item.price,
        0
      );
      const totalAmt =
        initialTotal +
        (order.gst || 0) +
        (order.deliveryCharge || 0) -
        (order.discount || 0);
      setFormData({
        _id: order._id || order.id,
        orderId: order.orderId || "",
        trainNo: order.trainNo || "",
        status: order.status || "",
        subTotal: initialTotal.toFixed(2),
        gst: order.gst || 0,
        discount: order.discount || 0,
        deliveryCharge: order.deliveryCharge || 0,
        totalAmount: totalAmt.toFixed(2),
        reason: order.reason || "",
        paymentStatus: order.paymentStatus || "",
        aggregator: order.aggregator || "",
        customerName: order.customerName || "",
        orderDate: order.orderDate || "",
        deliveryStation: order.deliveryStation || "",
        deliveryTime: order.deliveryDate || "",
      });
      setItems(processedItems);
    }
  }, [order]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: field === "qty" || field === "price" ? Number(value) : value,
      amount:
        field === "qty"
          ? value * updatedItems[index].price
          : field === "price"
          ? value * updatedItems[index].qty
          : updatedItems[index].amount,
    };
    setItems(updatedItems);

    // Update total amount
    const newTotal = updatedItems.reduce(
      (sum, item) => sum + item.qty * item.price,
      0
    );
    setFormData((prev) => ({
      ...prev,
      totalAmount: newTotal.toFixed(2),
    }));
  };

  const handleAddItem = () => {
    setItems([...items, { name: "", qty: 1, price: 0, amount: 0 }]);
  };

  const handleRemoveItem = (index) => {
    const updatedItems = items.filter((_, idx) => idx !== index);
    setItems(updatedItems);

    // Update total amount
    const newTotal = updatedItems.reduce(
      (sum, item) => sum + item.qty * item.price,
      0
    );
    setFormData((prev) => ({
      ...prev,
      totalAmount: newTotal.toFixed(2),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const processedItems = items.map((item) => ({
        ...item,
        qty: Number(item.qty) || 0,
        price: Number(item.price) || 0,
        amount: (Number(item.qty) || 0) * (Number(item.price) || 0),
      }));

      const calculatedTotal = processedItems.reduce(
        (sum, item) => sum + item.qty * item.price,
        0
      );

      const payload = {
        ...formData,
        totalAmount:
          calculatedTotal +
          parseFloat(formData.gst || 0) +
          parseFloat(formData.deliveryCharge || 0) +
          parseFloat(formData.discount || 0),
        items: processedItems,
      };

      // PUT request to update order
      await axios.put(`/orders/${formData._id}`, payload);
      alert("Order updated successfully!");
      if (onClose) onClose();
    } catch (error) {
      console.error("Error processing form:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form id="orderForm" onSubmit={handleSubmit}>
      {loading && (
        <div className="loading-overlay">
          <CircularProgress />
        </div>
      )}
      <div className="form-header">
        <h4>Edit Order Details</h4>
        <span id="formCloseBtn" className="close-btn" onClick={onClose}>
          &times;
        </span>
      </div>
      <div className="form-sections">
        <div className="form-body">
          <div className="form-group">
            <label htmlFor="trainNo">Train No</label>
            <input
              type="text"
              id="trainNo"
              className="form-control"
              value={formData.trainNo}
              onChange={handleChange}
              readOnly
            />
          </div>
          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              className="form-control"
              value={formData.status}
              onChange={handleChange}
            >
              <option value="">Select Status</option>

              <option value="cancelled">Cancelled</option>
              <option value="Order Confirmed">Order Confirmed</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="subTotal">Sub Total</label>
            <input
              type="text"
              id="subTotal"
              className="form-control"
              value={formData.subTotal}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="totalAmount">Total Amount</label>
            <input
              type="text"
              id="totalAmount"
              className="form-control"
              value={formData.totalAmount}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="paymentStatus">Payment Status</label>
            <input
              type="text"
              id="paymentStatus"
              className="form-control"
              value={formData.paymentStatus}
              onChange={handleChange}
              readOnly
            />
          </div>
          <div className="form-group">
            <label htmlFor="aggregator">Aggregator</label>
            <input
              type="text"
              id="aggregator"
              className="form-control"
              value={formData.aggregator}
              readOnly
            />
          </div>
          <div className="form-group">
            <label htmlFor="customerName">Customer Name</label>
            <input
              type="text"
              id="customerName"
              className="form-control"
              value={formData.customerName}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="orderDate">Order Date</label>
            <input
              type="text"
              id="orderDate"
              className="form-control"
              value={formData.orderDate}
              onChange={handleChange}
              readOnly
            />
          </div>
          {/* <div className="form-group">
          <label htmlFor="deliveryStation">Delivery Station</label>
          <input
            type="text"
            id="deliveryStation"
            className="form-control"
            value={formData.deliveryStation}
            onChange={handleChange}
          />
        </div> */}
          <div className="form-group">
            <label htmlFor="deliveryTime">Delivery Time</label>
            <input
              type="text"
              id="deliveryTime"
              className="form-control"
              value={formData.deliveryTime}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="reason">Reason</label>
            <select
              id="reason"
              className="form-control"
              value={formData.reason}
              onChange={handleChange}
            >
              <option value="">Select Reason</option>

              <option value="Customer Not Answered">
                Customer Not Answered
              </option>
              <option value="Customer Cancelled Order">
                Customer Cancelled Order
              </option>
              <option value="Train Already Departed">
                Train Already Departed
              </option>
              <option value="Train Delay / Late Arrival">
                Train Delay / Late Arrival
              </option>
              <option value="Restaurant Cancelled">Restaurant Cancelled</option>

              <option value="Order Missing Items">Order Missing Items</option>
              <option value="Restaurant Delay">Restaurant Delay</option>
              <option value="Order Packed, Awaiting Delivery">
                Order Packed, Awaiting Delivery
              </option>

              <option value="Train Missed">Train Missed</option>
              <option value="Delivered to Wrong Coach">
                Delivered to Wrong Coach
              </option>
              <option value="Delivered to Wrong Customer">
                Delivered to Wrong Customer
              </option>

              <option value="Successfully Delivered">
                Successfully Delivered
              </option>
              <option value="Delivered with Delay">Delivered with Delay</option>

              <option value="Customer Not Available">
                Customer Not Available
              </option>
              <option value="Phone Not Reachable">Phone Not Reachable</option>
              <option value="Train Rescheduled / Cancelled">
                Train Rescheduled / Cancelled
              </option>
              <option value="Delivery Partner Delay">
                Delivery Partner Delay
              </option>

              <option value="Refund Initiated">
                Refund Processed for Cancellation
              </option>
              <option value="Refund Completed to Customer">
                Refund Completed to Customer
              </option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        <div className="items-section">
          <div className="items-header">
            <label>Order Items</label>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleAddItem}
            >
              <i className="fas fa-plus"></i> Add Item
            </button>
          </div>
          <div className="items-list">
            {items.map((item, index) => (
              <div key={index} className="item-row">
                <div className="item-inputs">
                  <div className="form-group">
                    <label htmlFor={`item-name-${index}`}>Item Name</label>
                    <input
                      type="text"
                      id={`item-name-${index}`}
                      className="form-control"
                      value={item.name}
                      onChange={(e) =>
                        handleItemChange(index, "name", e.target.value)
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor={`item-qty-${index}`}>Quantity</label>
                    <input
                      type="number"
                      id={`item-qty-${index}`}
                      className="form-control"
                      value={item.qty}
                      min="1"
                      onChange={(e) =>
                        handleItemChange(index, "qty", e.target.value)
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor={`item-price-${index}`}>Price</label>
                    <input
                      type="number"
                      id={`item-price-${index}`}
                      className="form-control"
                      value={item.price}
                      min="0"
                      step="0.01"
                      onChange={(e) =>
                        handleItemChange(index, "price", e.target.value)
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label>Amount</label>
                    <input
                      type="text"
                      className="form-control"
                      value={(item.qty * item.price).toFixed(2)}
                      readOnly
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-danger remove-item"
                  onClick={() => handleRemoveItem(index)}
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="form-footer">
        <button type="submit" className="btn btn-primary" id="submitBtn">
          Save Changes
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          id="cancelBtn"
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
